import {
  AccountBalanceOutlined,
  AdminPanelSettingsOutlined,
  ArrowRightAlt,
  CompareArrowsOutlined,
  DashboardOutlined,
  DeviceHubOutlined,
  DnsOutlined,
  DynamicFeedOutlined,
  EqualizerOutlined,
  FormatListNumberedOutlined,
  GroupAddOutlined,
  GroupOutlined,
  Groups2Outlined,
  HistoryEduOutlined,
  HomeRepairServiceOutlined,
  InsertChartOutlined,
  ManageAccountsOutlined,
  MapOutlined,
  MonetizationOnOutlined,
  NumbersOutlined,
  PageviewOutlined,
  PersonAddAlt,
  PersonOutline,
  PersonOutlined,
  PlaylistAddCheckOutlined,
  PolicyOutlined,
  ReceiptOutlined,
  RequestQuoteOutlined,
  SchemaOutlined,
  SchoolOutlined,
  SupervisorAccountOutlined,
  TableViewOutlined,
  TrendingDownOutlined,
  TrendingUpOutlined,
  VideoLibraryOutlined,
  WalletOutlined,
} from '@mui/icons-material';
import { ReactComponent as IntegrityIcon } from '../../assets/integrity-i.svg';
import { uicolors } from '../../theme';
import {
  AgtStaffPermissions,
  AWS_GROUP,
  BillingPermissionData,
  BillingUserAccess,
} from '../../types';
import { ALLIANCE_APPS } from '../alliance/alliance-apps';

type ShareLink = {
  title: string;
  text: string;
  url: string;
};

export interface NavDataLink {
  title: string;
  // Only this Group can see this link
  hasGroup?: AWS_GROUP;
  // User must have one of the permissions
  permissions?: AWS_GROUP[];
  // Required permissions for agent staff users
  staffPermission?: AgtStaffPermissions;
  // to - for internal routing
  to?: string;
  // href - for external links
  href?: string;
  startIcon?: JSX.Element;
  children?: NavDataLink[];
  name?: BillingPermissionData['Name'];
  access?: BillingUserAccess[];
  shareLink?: ShareLink;
  hideFromProd?: boolean;
}

export interface NavDataSection {
  title: string;
  icon?: JSX.Element;
  // Only this Group can see this link
  hasGroup?: AWS_GROUP;
  // User must have one of the permissions
  permissions?: AWS_GROUP[];
  // Required permission for agent staff users
  staffPermission?: AgtStaffPermissions;
  links: NavDataLink[];
  name?: BillingPermissionData['Name'];
  access?: BillingUserAccess[];
}

export const navData: NavDataSection[] = [
  {
    title: 'Admin',
    icon: <AdminPanelSettingsOutlined color="primary" />,
    permissions: [
      AWS_GROUP.ReadAll,
      AWS_GROUP.Staff_ReadOnly,
      AWS_GROUP.Accounting,
      AWS_GROUP.CarrierFeeds,
      AWS_GROUP.Commissions,
      AWS_GROUP.CommissionApproval,
      AWS_GROUP.Marketing,
      AWS_GROUP.ARC_BillingSystem,
      AWS_GROUP.Contracting,
      AWS_GROUP.PermissionsAdmin,
      AWS_GROUP.Reconciliation,
    ],
    links: [
      {
        startIcon: <ManageAccountsOutlined fontSize="small" color="primary" />,
        title: 'Agent',
        to: '/admin/agent',
        permissions: [
          AWS_GROUP.ReadAll,
          AWS_GROUP.Contracting,
          AWS_GROUP.AgentDataAdmin,
          AWS_GROUP.Accounting,
          AWS_GROUP.Commissions,
          AWS_GROUP.CommissionApproval,
          AWS_GROUP.Staff_ReadOnly,
        ],
      },
      {
        title: 'Carrier Feeds',
        to: '/admin/carrier-feeds',
        startIcon: <DynamicFeedOutlined fontSize="small" color="primary" />,
        permissions: [
          AWS_GROUP.ReadAll,
          AWS_GROUP.GlobalAdmin,
          AWS_GROUP.CarrierFeeds,
          AWS_GROUP.Commissions,
          AWS_GROUP.CommissionApproval,
        ],
      },
      {
        title: 'Bank Reconciliation',
        to: '/admin/bank-reconciliation',
        startIcon: <CompareArrowsOutlined fontSize="small" color="primary" />,
        permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Reconciliation],
      },
      {
        title: 'Policy Manager',
        to: '/admin/policy-manager',
        startIcon: <PolicyOutlined fontSize="small" color="primary" />,
        permissions: [
          AWS_GROUP.ReadAll,
          AWS_GROUP.Commissions,
          AWS_GROUP.CommissionApproval,
          AWS_GROUP.Accounting,
        ],
      },
      {
        title: 'Matrix Manager',
        to: '/admin/matrix-manager',
        startIcon: <DnsOutlined fontSize="small" color="primary" />,
        permissions: [AWS_GROUP.ReadAll, AWS_GROUP.CommissionMatrix],
      },
      {
        title: 'Policy Lookup',
        to: '/admin/policy-lookup',
        startIcon: <PageviewOutlined fontSize="small" color="primary" />,
        permissions: [
          AWS_GROUP.ReadAll,
          AWS_GROUP.Commissions,
          AWS_GROUP.CommissionApproval,
          AWS_GROUP.Accounting,
        ],
      },
      {
        title: 'Accounting',
        startIcon: <AccountBalanceOutlined fontSize="small" color="primary" />,
        permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Accounting],
        children: [
          {
            to: '/admin/accounting/nacha',
            title: 'NACHA Manager',
            permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Accounting],
          },
          {
            to: '/admin/accounting/cb-manager',
            title: 'Percent CB Manager',
            permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Accounting],
          },
          {
            to: '/admin/accounting/adjustments',
            title: 'Adjustments',
            permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Accounting],
          },
          {
            to: '/admin/accounting/debts',
            title: 'Debts',
            permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Accounting],
          },
          {
            to: '/admin/accounting/reports',
            title: 'Reports',
            permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Accounting],
          },
          {
            to: '/admin/accounting/ach-returns',
            title: 'ACH Returns',
            permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Accounting],
          },
          {
            to: '/admin/accounting/1099-report',
            title: '1099 Report',
            permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Accounting],
          },
          {
            to: '/admin/accounting/unclaimed-properties',
            title: 'Unclaimed Properties',
            permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Accounting],
          },
          {
            to: '/admin/accounting/retained-earnings',
            title: 'Retained Earnings',
            permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Accounting],
          },
        ],
      },
      {
        title: 'CMS',
        to: '/admin/cms',
        startIcon: <DashboardOutlined fontSize="small" color="primary" />,
        permissions: [AWS_GROUP.ReadAll, AWS_GROUP.Marketing],
      },
      {
        title: 'Permissions',
        to: '/admin/permissions',
        startIcon: (
          <SupervisorAccountOutlined fontSize="small" color="primary" />
        ),
        permissions: [
          AWS_GROUP.ReadAll,
          AWS_GROUP.GlobalAdmin,
          AWS_GROUP.PermissionsAdmin,
        ],
      },
      {
        title: 'Billing',
        startIcon: <ReceiptOutlined fontSize="small" color="primary" />,
        hasGroup: AWS_GROUP.ARC_BillingSystem,
        children: [
          {
            title: 'Accounts Receivable',
            children: [
              {
                title: 'Accounts Receivable',
                to: '/admin/billing/accounts-receivable',
              },
              {
                title: 'Adjustment Approval',
                to: '/admin/billing/adjustment-approval',
                name: 'AR Adjustment',
                access: [BillingUserAccess['Allowed']],
              },
              {
                title: 'Payment Plan Management',
                to: '/admin/billing/payment-plan-management',
                name: 'Payment Plan Management',
                access: [
                  BillingUserAccess['Read-Only'],
                  BillingUserAccess['Read/Write'],
                ],
              },
            ],
          },
          {
            title: 'Drafts',
            children: [
              {
                title: 'Draft Adjustment Approvals',
                to: '/admin/billing/draft-adjustment-approvals',
                name: 'Draft Adjustment Approval',
                access: [BillingUserAccess['Allowed']],
              },
              {
                title: 'Create ACH',
                to: '/admin/billing/create-ach',
                name: 'Drafts',
                access: [BillingUserAccess['Allowed']],
              },
              {
                title: 'Post ACH',
                to: '/admin/billing/post-ach',
                name: 'Drafts',
                access: [BillingUserAccess['Allowed']],
              },
              {
                title: 'Credit Cards',
                to: '/admin/billing/credit-cards',
                name: 'Drafts',
                access: [BillingUserAccess['Allowed']],
              },
            ],
          },
          {
            title: 'Invoicing',
            children: [
              {
                title: 'Generate Invoices',
                to: '/admin/billing/generate-invoices',
                name: 'Invoicing',
                access: [BillingUserAccess['Allowed']],
              },
              {
                title: 'Invoice Line Items',
                to: '/admin/billing/invoice-line-items',
                name: 'Invoice Line Items',
                access: [
                  BillingUserAccess['Read-Only'],
                  BillingUserAccess['Read/Write'],
                ],
              },
            ],
          },
          {
            title: 'Reports',
            to: '/admin/billing/reports',
            access: [BillingUserAccess['Allowed']],
          },
          {
            title: 'Tools',
            children: [
              {
                title: 'Rollups',
                to: '/admin/billing/rollups',
                name: 'AR: Edit/Remove Entries',
                access: [BillingUserAccess['Allowed']],
              },
              {
                title: 'User Administration',
                to: '/admin/billing/user-administration',
                name: 'User Administration',
                access: [
                  BillingUserAccess['Read-Only'],
                  BillingUserAccess['Read/Write'],
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'My Business',
    icon: <DashboardOutlined color="primary" />,
    links: [
      {
        title: 'Commissions',
        to: '/business/commissions',
        startIcon: <TrendingUpOutlined fontSize="small" color="primary" />,
        staffPermission: AgtStaffPermissions['Commissions'],
      },
      {
        title: 'Debts',
        to: '/business/debts',
        startIcon: <TrendingDownOutlined fontSize="small" color="primary" />,
        staffPermission: AgtStaffPermissions['Debts'],
      },

      {
        title: 'My Wallet',
        href: `${ALLIANCE_APPS.contracting.url}/wallet`,
        startIcon: <WalletOutlined fontSize="small" color="primary" />,
        staffPermission: AgtStaffPermissions['Wallet'],
      },
      {
        title: 'My Profile',
        href: `${ALLIANCE_APPS.contracting.url}/account`,
        startIcon: <PersonOutlined fontSize="small" color="primary" />,
        staffPermission: AgtStaffPermissions['Profile'],
      },
      {
        title: 'Contracting',
        href: `${ALLIANCE_APPS.contracting.url}/home`,
        startIcon: <PolicyOutlined fontSize="small" color="primary" />,
      },
      {
        title: 'My Carrier Writing #s',
        href: `${ALLIANCE_APPS.contracting.url}/writing-numbers`,
        startIcon: <NumbersOutlined fontSize="small" color="primary" />,
      },
      {
        title: 'Integrity Connect',
        startIcon: (
          <IntegrityIcon
            style={{
              height: 16,
              width: 20,
              fill: uicolors.blue[500],
            }}
          />
        ),
        children: [
          {
            title: 'Register',
            href: 'https://integrity.com/',
          },
          {
            title: 'Quote/E-Apps',
            href: 'https://integrity.com/',
          },
        ],
      },
    ],
  },
  {
    title: 'My Dashboard',
    icon: <EqualizerOutlined fontSize="small" color="primary" />,
    links: [
      {
        title: 'Sales',
        to: '/business/dashboard/sales',
        startIcon: <MonetizationOnOutlined fontSize="small" color="primary" />,
      },
      {
        title: 'Recruiting',
        to: '/business/dashboard/recruiting',
        startIcon: <PersonAddAlt fontSize="small" color="primary" />,
      },
      {
        title: '$ by Direct Leg',
        to: '/business/dashboard/direct-leg',
        startIcon: <InsertChartOutlined fontSize="small" color="primary" />,
        staffPermission: AgtStaffPermissions['Commissions'],
      },
      {
        title: '$ Agent List',
        to: '/business/dashboard/agent-list',
        startIcon: (
          <FormatListNumberedOutlined fontSize="small" color="primary" />
        ),
        staffPermission: AgtStaffPermissions['Commissions'],
      },
      {
        title: 'My $ Chart',
        to: '/business/dashboard/deposit-summary',
        startIcon: <RequestQuoteOutlined fontSize="small" color="primary" />,
        staffPermission: AgtStaffPermissions['Commissions'],
      },
      {
        title: 'Team Sales',
        to: '/business/dashboard/team-sales',
        startIcon: <Groups2Outlined fontSize="small" color="primary" />,
      },
      {
        title: 'Team Recruits',
        to: '/business/dashboard/team-recruits',
        startIcon: <GroupAddOutlined fontSize="small" color="primary" />,
      },
      {
        title: 'Volume by Direct Leg',
        to: '/business/dashboard/volume-by-direct-leg',
        startIcon: <TableViewOutlined fontSize="small" color="primary" />,
      },
      {
        title: 'Recruiting by Direct Leg',
        to: '/business/dashboard/recruiting-by-direct-leg',
        startIcon: <DeviceHubOutlined fontSize="small" color="primary" />,
      },
    ],
  },
  {
    title: 'Team',
    icon: <GroupOutlined color="primary" />,
    links: [
      {
        title: 'Hierarchy Production Drawing',
        to: '/team/hierarchy-production-drawing',
        startIcon: (
          <SchemaOutlined
            fontSize="small"
            color="primary"
            sx={{ transform: 'rotate(-90deg)' }}
          />
        ),
      },
      {
        title: 'Hierarchy List',
        to: '/team/hierarchy',
        startIcon: (
          <FormatListNumberedOutlined fontSize="small" color="primary" />
        ),
      },
      {
        title: 'New Recruit Info',
        to: '/team/recruit',
        startIcon: <PersonOutline fontSize="small" color="primary" />,
      },
    ],
  },
  {
    title: 'Carriers',
    icon: <HistoryEduOutlined color="primary" />,
    links: [
      {
        title: 'Online Contracting',
        href: ALLIANCE_APPS.contracting.url,
        startIcon: <ArrowRightAlt fontSize="small" color="primary" />,
      },
    ],
  },
  {
    title: 'Resources',
    icon: <HomeRepairServiceOutlined color="primary" />,
    links: [
      {
        title: 'Welcome Videos',
        to: '/resources/welcome-videos',
        startIcon: <VideoLibraryOutlined fontSize="small" color="primary" />,
      },
      {
        title: 'Contest Flyers',
        to: '/resources/contest-flyers',
        startIcon: <MapOutlined fontSize="small" color="primary" />,
      },
      {
        title: 'Pre-Licensing',
        to: '/resources/pre-licensing',
        startIcon: (
          <PlaylistAddCheckOutlined fontSize="small" color="primary" />
        ),
      },
      {
        title: 'Continuing Education',
        href: 'https://www.webce.com/',
        startIcon: <SchoolOutlined fontSize="small" color="primary" />,
      },
    ],
  },
];
